import React, { useCallback, useEffect, useMemo, useState } from 'react';

function formatDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatCadence(reminder) {
  if (reminder.cadencePreset === 'custom') {
    const n = Number(reminder.intervalValue) || 1;
    const unit = String(reminder.intervalUnit || 'week');
    return `Every ${n} ${unit}${n > 1 ? 's' : ''}`;
  }
  const labels = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    annually: 'Annually'
  };
  return labels[reminder.cadencePreset] || reminder.cadencePreset;
}

const STANDARD_CATEGORIES = ['Education', 'Food', 'Medical', 'Other'];

export function collectDonorCategories(beneficiaries) {
  const set = new Set();
  for (const b of beneficiaries || []) {
    for (const need of b.needs || []) {
      if (need.category) set.add(need.category);
    }
  }
  const fromSite = Array.from(set).sort();
  if (fromSite.length) return fromSite;
  return [...STANDARD_CATEGORIES];
}

function formatRequestLabel(reminder) {
  if (reminder.category) return reminder.category;
  if (reminder.beneficiaryName) return reminder.beneficiaryName;
  return 'Any request';
}

function emptyForm(userEmail) {
  return {
    category: '',
    cadencePreset: 'weekly',
    customValue: '1',
    customUnit: 'week',
    channelSms: false,
    channelEmail: true,
    phone: '',
    email: userEmail || ''
  };
}

function formFromReminder(reminder, userEmail) {
  return {
    category: reminder.category || '',
    cadencePreset: reminder.cadencePreset || 'weekly',
    customValue: String(reminder.intervalValue || 1),
    customUnit: reminder.intervalUnit || 'week',
    channelSms: Boolean(reminder.channels?.sms),
    channelEmail: reminder.channels?.email !== false,
    phone: reminder.phone || '',
    email: reminder.email || userEmail || ''
  };
}

function Field({ id, label, hint, children, className = '' }) {
  return (
    <div className={`reminder-field ${className}`.trim()}>
      <label htmlFor={id} className="reminder-field__label">
        {label}
      </label>
      {children}
      {hint ? <span className="reminder-field__hint">{hint}</span> : null}
    </div>
  );
}

export default function RemindersTab({ beneficiaries, user, fetchJSON, onNotice }) {
  const [serverCategories, setServerCategories] = useState(null);
  const categories = useMemo(() => {
    const fromSite = collectDonorCategories(beneficiaries);
    if (Array.isArray(serverCategories) && serverCategories.length) {
      const merged = new Set([...serverCategories, ...fromSite]);
      return Array.from(merged).sort();
    }
    return fromSite;
  }, [beneficiaries, serverCategories]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(user?.email));

  const showCustom = form.cadencePreset === 'custom';

  const loadReminders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchJSON('/reminders');
      setReminders(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err.message || 'Could not load reminders');
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, [fetchJSON]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await fetchJSON('/beneficiaries/request-categories');
        if (!cancelled && Array.isArray(payload.categories) && payload.categories.length) {
          setServerCategories(payload.categories);
        }
      } catch {
        /* use site-derived categories */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchJSON]);

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm(user?.email));
  }

  function startEdit(reminder) {
    setEditingId(reminder.id);
    setForm(formFromReminder(reminder, user?.email));
    setError('');
  }

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function toggleChannel(channel) {
    setForm((prev) => ({
      ...prev,
      [channel === 'sms' ? 'channelSms' : 'channelEmail']:
        !prev[channel === 'sms' ? 'channelSms' : 'channelEmail']
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const body = {
      category: form.category || null,
      cadencePreset: form.cadencePreset,
      channels: {
        sms: form.channelSms,
        email: form.channelEmail
      },
      phone: form.phone.trim(),
      email: form.email.trim()
    };
    if (form.cadencePreset === 'custom') {
      body.customValue = Number(form.customValue);
      body.customUnit = form.customUnit;
    }

    setSaving(true);
    try {
      if (editingId) {
        await fetchJSON(`/reminders/${encodeURIComponent(editingId)}`, {
          method: 'PATCH',
          body: JSON.stringify(body)
        });
        onNotice?.('Reminder updated.');
      } else {
        await fetchJSON('/reminders', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        onNotice?.('Reminder saved.');
      }
      resetForm();
      await loadReminders();
    } catch (err) {
      setError(err.message || 'Could not save reminder');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(reminderId) {
    if (!window.confirm('Delete this reminder?')) return;
    setError('');
    try {
      await fetchJSON(`/reminders/${encodeURIComponent(reminderId)}`, { method: 'DELETE' });
      if (editingId === reminderId) resetForm();
      await loadReminders();
      onNotice?.('Reminder deleted.');
    } catch (err) {
      setError(err.message || 'Could not delete reminder');
    }
  }

  return (
    <section className="reminders-section">
      <header className="reminders-section__header">
        <h2 className="giving-panel-title">Reminders</h2>
        <p className="giving-panel-hint">
          Set when and how you would like to be nudged to give again. Saved reminders appear below.
        </p>
      </header>

      {error ? <div className="error-message">{error}</div> : null}

      <form
        className={`reminders-form-card${editingId ? ' reminders-form-card--editing' : ''}`}
        onSubmit={handleSubmit}
      >
        {editingId ? (
          <div className="reminders-form-card__badge">
            <span className="reminders-form-card__badge-dot" aria-hidden />
            Editing reminder
          </div>
        ) : null}

        <div className="reminders-form-card__grid">
          <div className="reminders-form-block">
            <h3 className="reminders-form-block__title">Schedule</h3>
            <div className="reminders-form-block__fields">
              <Field id="reminder-category" label="Request category" className="reminder-field--grow">
                <div className="reminder-control reminder-control--select">
                  <select
                    id="reminder-category"
                    className="reminder-input"
                    value={form.category}
                    onChange={(e) => updateField('category', e.target.value)}
                  >
                    <option value="">Any request</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>

              <Field id="reminder-cadence" label="Frequency" className="reminder-field--cadence">
                <div className="reminder-control reminder-control--select">
                  <select
                    id="reminder-cadence"
                    className="reminder-input"
                    value={form.cadencePreset}
                    onChange={(e) => updateField('cadencePreset', e.target.value)}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </Field>

              {showCustom ? (
                <div className="reminder-repeat-group" role="group" aria-label="Custom repeat interval">
                  <span className="reminder-repeat-group__label">Repeat every</span>
                  <div className="reminder-control reminder-control--number">
                    <input
                      id="reminder-custom-value"
                      className="reminder-input reminder-input--number"
                      type="number"
                      min="1"
                      step="1"
                      value={form.customValue}
                      onChange={(e) => updateField('customValue', e.target.value)}
                      aria-label="Repeat every number"
                    />
                  </div>
                  <div className="reminder-control reminder-control--select">
                    <select
                      id="reminder-custom-unit"
                      className="reminder-input"
                      value={form.customUnit}
                      onChange={(e) => updateField('customUnit', e.target.value)}
                      aria-label="Repeat interval unit"
                    >
                      <option value="day">Days</option>
                      <option value="week">Weeks</option>
                      <option value="month">Months</option>
                      <option value="year">Years</option>
                    </select>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="reminders-form-block reminders-form-block--notify">
            <h3 className="reminders-form-block__title">Notifications</h3>
            <div className="reminders-form-block__fields reminders-form-block__fields--notify">
              <div className="reminder-channel-toggles" role="group" aria-label="Notification channels">
                <button
                  type="button"
                  className={`reminder-channel-pill${form.channelSms ? ' is-active' : ''}`}
                  aria-pressed={form.channelSms}
                  onClick={() => toggleChannel('sms')}
                >
                  <span className="reminder-channel-pill__icon" aria-hidden>
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path
                        fill="currentColor"
                        d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm-2 12H6v-2h12v2Zm0-3H6V9h12v4Z"
                      />
                    </svg>
                  </span>
                  SMS
                </button>
                <button
                  type="button"
                  className={`reminder-channel-pill${form.channelEmail ? ' is-active' : ''}`}
                  aria-pressed={form.channelEmail}
                  onClick={() => toggleChannel('email')}
                >
                  <span className="reminder-channel-pill__icon" aria-hidden>
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path
                        fill="currentColor"
                        d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5.99 2L4 8v10h16V8Zm-8 3.99L4 6h16l-8 5.99Z"
                      />
                    </svg>
                  </span>
                  Email
                </button>
              </div>

              {form.channelSms ? (
                <Field id="reminder-phone" label="Mobile number" className="reminder-field--contact">
                  <div className="reminder-control">
                    <input
                      id="reminder-phone"
                      className="reminder-input"
                      type="tel"
                      placeholder="+254 712 345 678"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      autoComplete="tel"
                    />
                  </div>
                </Field>
              ) : null}

              {form.channelEmail ? (
                <Field id="reminder-email" label="Email address" className="reminder-field--contact">
                  <div className="reminder-control">
                    <input
                      id="reminder-email"
                      className="reminder-input"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                </Field>
              ) : null}
            </div>
          </div>

          <div className="reminders-form-actions">
            {editingId ? (
              <button type="button" className="reminder-btn reminder-btn--ghost" onClick={resetForm}>
                Cancel
              </button>
            ) : null}
            <button className="reminder-btn reminder-btn--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add reminder'}
            </button>
          </div>
        </div>
      </form>

      <div className="reminders-list-panel">
        {loading ? (
          <p className="reminders-list-empty">Loading reminders…</p>
        ) : !reminders.length ? (
          <p className="reminders-list-empty">No reminders set yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table giving-table reminders-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Cadence</th>
                  <th>Channels</th>
                  <th>Contact</th>
                  <th>Next reminder</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reminders.map((r) => {
                  const isEditing = editingId === r.id;
                  const contact = [
                    r.channels?.sms && r.phone ? `SMS: ${r.phone}` : null,
                    r.channels?.email && r.email ? `Email: ${r.email}` : null
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <tr key={r.id} className={isEditing ? 'reminders-table-row--editing' : ''}>
                      <td data-label="Request">{formatRequestLabel(r)}</td>
                      <td data-label="Cadence">{formatCadence(r)}</td>
                      <td data-label="Channels">
                        {[r.channels?.sms ? 'SMS' : null, r.channels?.email ? 'Email' : null]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </td>
                      <td data-label="Contact">{contact || '—'}</td>
                      <td data-label="Next reminder">{formatDate(r.nextReminderAt)}</td>
                      <td data-label="Actions">
                        <div className="reminders-row-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-table-action"
                            onClick={() => startEdit(r)}
                            disabled={isEditing}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-table-action reminders-btn-delete"
                            onClick={() => handleDelete(r.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
